import React from 'react';
import { Form } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import { FilterOperator } from 'react-epharma-components';
import { CNPJ_ALPHA_MASK, isValid, sanitize } from '../utils/cnpj-utils';

// Implementacao propria de input de CNPJ alfanumerico que NAO depende de BaseInput.
// O BaseInput da react-epharma-components 1.3.x repassa a prop `mask` para o ref;
// o BaseForm copia esse `mask` para o filtro e o BaseGrid, ao montar o request,
// remove tudo que nao for digito (/[^\d]+/g) quando ha mascara. Como o CNPJ alfa
// tem letras, o filtro sai sem letras e a busca retorna vazia/errada.
//
// Estrategia: este componente nao expoe `mask` em props. O BaseForm le `props.mask`
// como undefined, o BaseGrid cai no caminho que envia o valor cru, e a sanitizacao
// (apenas remover pontuacao da mascara) acontece aqui em getValue() via sanitize().

interface ICnpjAlphaInputProps {
  name?: string;
  label?: string;
  required?: boolean;
  excludeFilter?: boolean;
  filterOperator?: FilterOperator;
  feedback?: { invalid: string; valid?: string };
  // Permite props extras (className, style, etc) sem listar uma a uma
  [key: string]: any;
}

interface ICnpjAlphaInputState {
  value: string;
  isInvalid: boolean;
  disabled: boolean;
}

// Contrato esperado pelo BaseForm/BaseGrid da react-epharma-components:
//   instance.getValue()        - usado no filtro e na validacao
//   instance.getDisplayValue() - usado no badge do toolbar
//   instance.setValue(v)       - usado em identifier-fill e reset programatico
//   instance.setIsInvalid(b)   - usado em validateForm
//   instance.state.isInvalid   - lido diretamente em validateForm
//   instance.state.disabled    - lido em getReadDto/getUpdateDto
//   instance.props.name        - propriedade do filtro
//   instance.props.filterOperator
//   instance.props.label
//   instance.props.excludeFilter
class CnpjAlphaInput extends React.Component<ICnpjAlphaInputProps, ICnpjAlphaInputState> {
  static defaultProps: Partial<ICnpjAlphaInputProps> = {
    label: 'CNPJ',
    name: 'cnpj',
    feedback: { invalid: 'Informe um CNPJ válido' },
  };

  state: ICnpjAlphaInputState = {
    value: '',
    isInvalid: false,
    disabled: false,
  };

  getValue = (): string => sanitize(this.state.value);
  getDisplayValue = (): string => this.state.value;
  getRawValue = (): string => this.state.value;

  setValue = (value: any) => {
    this.setState({ value: value == null ? '' : String(value) });
  };

  setIsInvalid = (isInvalid: boolean) => this.setState({ isInvalid });
  setIsValid = (_isValid: boolean) => { /* sem feedback positivo customizado */ };
  setDisabled = (disabled: boolean) => this.setState({ disabled });
  reset = () => this.setState({ value: '', isInvalid: false });

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ value: event.target.value });
    if (this.props.onChange) this.props.onChange(event);
  };

  handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    this.setState({ isInvalid: !!raw && !isValid(raw) });
    if (this.props.onBlur) this.props.onBlur(event);
  };

  render() {
    const { name, label, required, feedback } = this.props;
    const { value, isInvalid, disabled } = this.state;

    return (
      <Form.Group>
        <Form.Label htmlFor={name}>
          {label}
          {required && <span className="text-danger">&nbsp;*</span>}
        </Form.Label>
        <MaskedInput
          mask={CNPJ_ALPHA_MASK}
          guide={false}
          id={name}
          name={name}
          value={value}
          disabled={disabled}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          className={`form-control form-control-sm ${isInvalid ? 'is-invalid' : ''}`}
        />
        {isInvalid && (
          <Form.Control.Feedback type="invalid">
            {feedback?.invalid || 'Informe um CNPJ válido'}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  }
}

export default CnpjAlphaInput;
